<template>
  <div class="order-confirm">
    <van-nav-bar :fixed="true" z-index="9" :title="$t('orderPage.reviewRentalPlan')" :border="false" left-arrow @click-left="onClickLeft" />
    <template v-if="showData">
      <div class="order-confirm__safety order-confirm__card">
        <Icon name="shield-o" class="order-confirm__safety-icon" aria-hidden="true" />
        <div>
          <div class="order-confirm__safety-title">{{ $t('orderPage.orderSafeTitle') }}</div>
          <div class="order-confirm__safety-subtitle">{{ $t('orderPage.orderSafeSubtitle') }}</div>
        </div>
      </div>

      <section class="order-confirm__address order-confirm__card">
        <div class="order-confirm__section-title">
          <div class="order-confirm__section-title-main">
            <Icon v-if="address" name="location-o" />
            <span>{{ $t('orderPage.deliveryAddress') }}</span>
          </div>
          <button v-if="address" type="button" class="order-confirm__edit" @click="openAddressPopup({ showType: 2 })" :aria-label="$t('edit')">
            <span>{{ $t('edit') }}</span>
            <Icon name="arrow" />
          </button>
        </div>
        <div class="order-confirm__divider"></div>
        <div class="btn-primary sm-radius" @click="openAddressPopup" v-if="!address">
          <Icon name="plus" class="mg-r-12" />
          {{ $t('addressPop.add') }}
        </div>
        <div class="address-info" @click="openAddressPopup({ showType: 2 })" v-else>
          <div class="address-info__text">
            <div class="address-info__name">{{ address.recipientName }}</div>
            <div class="address-info__phone">{{ formatIndonesiaPhone(address.recipientPhone) }}</div>
            <div class="address-info__detail">{{ addressDetail }}</div>
          </div>
        </div>
      </section>

      <div v-if="contactCountState === 'loading'" class="contact-count-state">
        {{ $t('loading') }}
      </div>
      <div v-else-if="contactCountState === 'failed'" class="contact-count-state contact-count-state--error">
        <div>{{ $t('systemError') }}</div>
        <div class="btn-primary btn-normal" @click="queryNeedContactCount">{{ $t('retry') }}</div>
      </div>

      <section class="order-confirm__contact order-confirm__card" v-if="showContact">
        <div class="order-confirm__section-title">
          <div class="order-confirm__section-title-main">
            <Icon v-if="showData.emergencyContactList.length" name="contact" />
            <span>{{ $t('orderPage.referenceContacts') }}</span>
          </div>
          <button v-if="showData.emergencyContactList.length" type="button" class="order-confirm__edit" @click="openContactPopup" :aria-label="$t('edit')">
            <span>{{ $t('edit') }}</span>
            <Icon name="arrow" />
          </button>
        </div>
        <div class="order-confirm__divider"></div>
        <div class="contact-info flex align-start" @click="openContactPopup" v-if="showData.emergencyContactList.length">
          <div class="contact-info__text">
            <div class="contact-item" v-for="(value, index) in showData.emergencyContactList" :key="value.id || value.phone || index">
              <div class="contact-item__name">{{ contactNameText(value) }}</div>
              <div class="contact-item__phone">{{ formatIndonesiaPhone(value.phone || value.mobile) }}</div>
            </div>
          </div>
        </div>
        <div v-else class="contact-info__required contact-info__required--empty">
          {{ $tc('contactPop.selectRequired', needContactCount, { count: needContactCount }) }}
        </div>
        <div class="order-confirm__contact-action btn-primary sm-radius" @click="openContactPopup" v-if="!showData.emergencyContactList.length">
          <Icon name="plus" class="mg-r-12" />
          {{ $t('orderPage.addReferenceContacts') }}
        </div>
      </section>

      <section class="order-confirm__goods order-confirm__card">
        <div class="order-confirm__section-title">
          <div class="order-confirm__section-title-main">
            <Icon name="phone-o" />
            <span>{{ $t('orderPage.product') }}</span>
          </div>
        </div>
        <div class="order-confirm__divider"></div>
        <div class="flex">
          <div class="order-confirm__goods-info-img" :class="{ 'is-empty': goodsImageFailed || !showData.imageUrl }">
            <img
              v-if="showData.imageUrl && !goodsImageFailed"
              :src="showData.imageUrl"
              :alt="showData.goodsName"
              @error="onGoodsImageError"
            >
          </div>
          <div class="order-confirm__goods-info-copy mg-l-20 flex-1">
            <div class="order-confirm__goods-info-name text-ellipsis">{{ showData.goodsName }}</div>
            <div class="order-confirm__goods-info-sku">{{ showData.skuStr }}</div>
          </div>
        </div>
      </section>

      <section class="order-confirm__dates order-confirm__card">
        <div class="order-confirm__section-title">
          <div class="order-confirm__section-title-main">
            <Icon name="underway-o" />
            <span>{{ $t('orderPage.rentalDates') }}</span>
          </div>
        </div>
        <div class="order-confirm__divider"></div>
        <div class="flex justify-between align-center goods-plan">
          <div class="goods-plan__left">
            <div class="desc">{{ $t('orderPage.rentalStartDateDetail') }}</div>
            <div class="date">{{ showData.rentalStartDate | rentalDateFormat }}</div>
            <div class="estimated">{{ $t('orderPage.rentalDateEstimated') }}</div>
          </div>
          <div class="goods-plan__arrow"></div>
          <div class="goods-plan__right">
            <div class="desc">{{ $t('orderPage.returnDateDetail') }}</div>
            <div class="date">{{ showData.rentalEndDate | rentalDateFormat }}</div>
            <div class="estimated">{{ $t('orderPage.rentalDateEstimated') }}</div>
          </div>
        </div>
        <div class="goods-plan__tips"><Icon name="info-o" />{{ $t('orderPage.rentalStartAdjusted') }}</div>
      </section>

      <section class="order-confirm__info order-confirm__card">
        <div class="order-confirm__section-title">
          <div class="order-confirm__section-title-main">
            <Icon name="orders-o" />
            <span>{{ $t('orderPage.pricePlan') }}</span>
          </div>
        </div>
        <div class="order-confirm__divider"></div>
        <div class="flex justify-between align-center order-confirm__price-row">
          <div class="text-left">{{ $t('orderPage.firstRentalAmount') }}</div>
          <div class="text-right">{{ priceUnit }}{{ showData.firstRentalAmount | numberFormat }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20 order-confirm__price-row">
          <div class="text-left">{{ periodicRentLabel }}</div>
          <div class="text-right">{{ priceUnit }}{{ showData.planPrice | numberFormat }}/{{ periodicRentUnit }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20 order-confirm__price-row">
          <div class="text-left">{{ $t('orderPage.rentalTerm') }}</div>
          <div class="text-right">{{ showData.rentalTerm }} {{ showData.rentalCycle }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20 order-confirm__price-row">
          <div class="text-left">
            {{ $t('orderPage.deposit') }}
            <small>{{ $t('orderPage.refundableParenthetical') }}</small>
          </div>
          <div class="text-right">{{ priceUnit }}{{ showData.depositAmount | numberFormat }}</div>
        </div>
        <div class="line-bottom mg-t-20"></div>
        <div class="flex justify-between align-center mg-t-20 order-confirm__price-row order-confirm__price-row--total">
          <div class="text-left">{{ $t('orderPage.initialPaymentAmount') }}</div>
          <div class="text-right">{{ priceUnit }}{{ showData.firstPayAmount | numberFormat }}</div>
        </div>
        <button type="button" class="order-confirm__plan-link" @click="showRepayPlanDialog = true">
          <Icon name="orders-o" />
          <span>{{ $t('planDetail.title') }}</span>
          <Icon name="arrow" />
        </button>
        <div class="order-confirm__remaining-notice">
          <Icon name="shield-o" class="order-confirm__remaining-notice-icon" aria-hidden="true" />
          <div>
            <div class="order-confirm__remaining-notice-title">{{ $t('orderPage.remainingAmountNoticeTitle') }}</div>
            <div class="order-confirm__remaining-notice-desc">{{ $t('orderPage.remainingAmountNoticeDesc') }}</div>
          </div>
        </div>
      </section>

      <section class="order-confirm__notes-card order-confirm__card">
        <div class="order-confirm__notes-heading">
          <Icon name="shield-o" aria-hidden="true" />
          <span>{{ $t('orderPage.ownershipNoticeTitle') }}</span>
        </div>
        <div class="order-confirm__divider"></div>
        <div class="order-confirm__notes-body">{{ $t('orderPage.ownershipNotice') }}</div>
      </section>

      <section class="order-confirm__notes-card order-confirm__card">
        <div class="order-confirm__notes-heading">
          <Icon name="notes-o" aria-hidden="true" />
          <span>{{ $t('orderPage.importantNotesTitle') }}</span>
        </div>
        <div class="order-confirm__divider"></div>
        <ul class="order-confirm__notes-list">
          <li>{{ $t('orderPage.importantNotes1') }}</li>
          <li>{{ $t('orderPage.importantNotes2') }}</li>
          <li>{{ $t('orderPage.importantNotes3') }}</li>
        </ul>
      </section>

      <section ref="agreementSection" class="order-confirm__agreement order-confirm__card" v-if="agreements.length > 0">
        <div class="order-confirm__section-title">
          <div class="order-confirm__section-title-main">
            <Icon name="description" />
            <span>{{ $t('orderPage.agreementTitle') }}</span>
          </div>
        </div>
        <div class="order-confirm__divider"></div>
        <div class="agreement-check">
          <van-checkbox
            v-model="agreementChecked"
            shape="round"
            icon-size="20"
            class="agreement-checkbox"
            :aria-label="$t('orderPage.agreementCheckLabel')"
          />
          <span class="sr-only">{{ $t('orderPage.agreementCheckLabel') }}</span>
          <div class="agreement-summary">
            {{ $t('orderPage.agText') }}
            <template v-for="(ag, index) in agreements.slice(0, 2)">
              <template v-if="index > 0">{{ ', ' }}</template>
              <span :key="ag.id || ag.code || index" @click="goToAgreement(ag)">{{ ag.title }}</span>
            </template>
            <button
              v-if="agreements.length > 2"
              type="button"
              class="agreement-more"
              @click="agreementExpanded = !agreementExpanded"
            >
              <span class="agreement-more-prefix">, {{ $t('orderPage.agLastText') }} </span>
              {{ $t('orderPage.moreAgreements', { count: agreements.length - 2 }) }}
            </button>
          </div>
        </div>
        <div v-if="agreementExpanded && agreements.length > 2" class="agreement-extra">
          <span v-for="(ag, index) in agreements.slice(2)" :key="ag.id || ag.code || `extra-${index}`" @click="goToAgreement(ag)">{{ ag.title }}</span>
        </div>
      </section>

      <div class="order-confirm__footer">
        <div class="footer-amount">
          <span>{{ $t('orderPage.initialPaymentAmount') }}</span>
          <strong>{{ priceUnit }}{{ showData.firstPayAmount | numberFormat }}</strong>
        </div>
        <div class="footer-description">
          {{ $t('orderPage.firstMonthRentSecurityDeposit') }}
        </div>
        <div class="btn-primary" :class="{ 'is-disabled': prechecking }" @click="goCheckout">
          {{ $t('orderPage.choosePaymentMethod') }}
        </div>
        <div class="checkout-hint">{{ $t('orderPage.checkoutHint') }}</div>
      </div>
    </template>

    <AddressPopup :key="componentKey" ref="addressPopup" @change="onAddressConfirm" @delete="onAddressDelete" :defaultSelectId="address && address.id" />
    <ContactPopup
      v-if="showContact"
      ref="contactPopup"
      :needCount="needContactCount"
      :defaultContact="showData && showData.emergencyContactList"
      :userPhone="userPhone"
      @success="onContactSave"
      @selection-change="onContactSelectionChange"
    />
    <KycPopup ref="kycPopup" />
    <AuthResultPopup
      ref="authResultPopup"
      @success="handleAuthResultSuccess"
      @retry="handleAuthResultRetry"
      @back="handleAuthResultBack"
      @close="clearAuthResultQuery"
    />
    <Popup v-model="showRepayPlanDialog" position="center" round class="order-confirm__repay-plan-popup">
      <div class="order-confirm__plan-sheet">
        <div class="order-confirm__plan-sheet-header">
          <strong>{{ $t('orderPage.rentalPlan') }}</strong>
          <Icon name="cross" @click="showRepayPlanDialog = false" />
        </div>
        <div class="order-confirm__plan-sheet-body">
          <div class="order-confirm__plan-sheet-notice">
            <span class="order-confirm__plan-sheet-notice-icon"><Icon name="calendar-o" /></span>
            <span><strong>{{ $t('planDetail.repayPlanMonthlyTipTitle') }}</strong><small>{{ $t('planDetail.repayPlanMonthlyTip') }}</small></span>
          </div>
          <div v-if="!repayPlanPreviewItems.length" class="order-confirm__plan-sheet-state">{{ $t('planDetail.repayPlanEmpty') }}</div>
          <div v-else class="order-confirm__plan-list">
            <div v-for="item in repayPlanPreviewItems" :key="`confirm-plan-${item.period}`" class="order-confirm__plan-item">
              <strong class="order-confirm__plan-period">{{ repayPlanPeriodText(item.period) }}</strong>
              <div class="order-confirm__plan-row">
                <span>{{ $t('orderPage.repayPlanDueDate') }}</span>
                <strong>{{ item.paidTime | rentalDateFormat }}</strong>
              </div>
              <div class="order-confirm__plan-row">
                <span>{{ $t('orderPage.repayPlanAmount') }}</span>
                <strong class="order-confirm__plan-amount">{{ priceUnit }}{{ item.total | numberFormat }}</strong>
              </div>
            </div>
          </div>
          <div class="order-confirm__plan-sheet-notice order-confirm__plan-sheet-notice--payment">
            <span class="order-confirm__plan-sheet-notice-icon"><Icon name="shield-o" /></span>
            <span><strong>{{ $t('planDetail.repayPlanPaymentTipTitle') }}</strong><small>{{ $t('planDetail.repayPlanPaymentTip') }}</small></span>
          </div>
        </div>
      </div>
    </Popup>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Checkbox, Icon, Popup } from 'vant';
import AddressPopup from '@/components/AddressPopup/index.vue';
import ContactPopup from '@/components/ContactPopup/index.vue';
import { getContactCount } from '@/api/order';
import { getAgreements, getAppSystemConfig } from '@/api/common';
import { getAuthResult } from '@/api/kyc';
import {
  clearFinishedKycV2,
  completeKycV2Flow,
  openKycV2ResultPopup,
  retryKycV2Flow,
  startKycV2Flow
} from '@/utils/kycV2Flow';
import { hasKycV2Context, isKycV2Return } from '@/utils/kycV2';
import { queryAdditionalInfo } from '@/api/additionalInfo';
import { resolveAssetUrl } from '@/common/product';
import { AGREEMENT_SCENE, LANGUAGE, RELATIONSHIP_TYPE, RENTAL_CYCLE_DAYS } from '@/common/constants';
import { canSkipOrderAdditional, resolveRequiredSupplementTypes } from '@/common/supplement';
import { formatAddressForOrder } from '@/utils/address';
import agreement from '@/mixins/agreement';
import routerBack from '@/mixins/routerBack';
import KycPopup from '@/components/KycPopup/index.vue';
import AuthResultPopup from '@/components/AuthResultPopup/index.vue';
import dayjs from 'dayjs';
import { DEFAULT_REG_CHANNEL } from '@/config';

const KYC_AUTH_TOKEN_KEY = 'checkoutKycAuthToken';
const TEMP_ORDER_KEY = 'checkoutTempOrderData';
const KYC_VERIFIED_KEY = 'checkoutKycVerified';

const addMoney = (...amounts) => amounts.reduce((total, amount) => {
  return total + Math.round((Number(amount) || 0) * 100);
}, 0) / 100;

const ORDER_CONFIRM_AGREEMENT_PRIORITY = {
  'device rental agreement': 0,
  'personal data processing consent': 1,
  'data processing consent': 1,
  'privacy policy': 2,
};

const normalizeAgreementTitle = title => String(title || '')
  .trim()
  .toLowerCase()
  .replace(/[.,:;!?]+/g, '')
  .replace(/\s+/g, ' ');

const orderConfirmAgreements = agreements => (Array.isArray(agreements) ? agreements : [])
  .map((agreement, index) => ({
    agreement,
    index,
    priority: ORDER_CONFIRM_AGREEMENT_PRIORITY[normalizeAgreementTitle(agreement?.title)] ?? 100,
  }))
  .sort((left, right) => left.priority - right.priority || left.index - right.index)
  .map(item => item.agreement);

export default {
  name: 'OrderConfirm',
  mixins: [agreement, routerBack],
  components: {
    AddressPopup,
    ContactPopup,
    'van-checkbox': Checkbox,
    Icon,
    KycPopup,
    AuthResultPopup,
    Popup
  },
  data() {
    return {
      agreements: [],
      componentKey: Math.random(),
      needContactCount: 0,
      contactCountState: 'idle',
      agreementChecked: false,
      agreementExpanded: false,
      goodsImageFailed: false,
      prechecking: false,
      showRepayPlanDialog: false
    }
  },
  computed: {
    ...mapState({
      tempOrderData: state => state.orders.tempOrderData || {},
      channel: state => state.user.channel || DEFAULT_REG_CHANNEL,
      userPhone: state => state.user.userInfo?.phoneNumber || '',
      addressList: state => state.user.addressList || []
    }),
    showData() {
      const {
        goodsInfo,
        sku = {},
        skuStr,
        firstPayAmount,
        firstRentalAmount,
        riskAmount,
        depositAmount,
        planPrice,
        planIndex,
        rentalTerm,
        rentDate,
        backDate,
        address,
        emergencyContactList = []
      } = this.tempOrderData;
      if (!goodsInfo) {
        return null;
      }
      const rentalCycleCode = sku.rentalCycle || this.tempOrderData.planUnit || '';
      return {
        goodsName: goodsInfo.goodName,
        imageUrl: resolveAssetUrl(sku.imageUrl || goodsInfo.imageUrl || goodsInfo.goodsPic || goodsInfo.imgUrl),
        skuStr,
        firstPayAmount,
        firstRentalAmount: firstRentalAmount || addMoney(planPrice, riskAmount || this.tempOrderData.riskAmount || 0),
        riskAmount: riskAmount || this.tempOrderData.riskAmount || 0,
        totalRentalPayments: addMoney(Number(planPrice) * (Number(rentalTerm) || 0), riskAmount || this.tempOrderData.riskAmount || 0),
        depositAmount,
        planPrice,
        remainingRent: planIndex - 1,
        rentalTerm,
        rentalStartDate: rentDate,
        rentalEndDate: backDate,
        rentalCycleCode,
        rentalCycle: this.$t(`cycle.${rentalCycleCode}`),
        address,
        emergencyContactList
      };
    },
    address() {
      return this.showData?.address || null;
    },
    addressDetail() {
      if (!this.address) {
        return '';
      }
      return formatAddressForOrder(this.address);
    },
    priceUnit() {
      return this.$t('priceUnit');
    },
    normalizedRentalCycleCode() {
      const cycle = this.normalizeCycle(this.showData?.rentalCycleCode);
      if (['WEEK', 'WEEKLY', 'W'].includes(cycle)) return 'WEEKLY';
      if (['BI_WEEK', 'BIWEEKLY', 'BI-WEEKLY', 'BI_WEEKLY', 'B'].includes(cycle)) return 'BI_WEEKLY';
      if (['MONTH', 'MONTHLY', 'M'].includes(cycle)) return 'MONTHLY';
      return 'MONTHLY';
    },
    periodicRentLabel() {
      const cycle = this.normalizedRentalCycleCode;
      return this.$t(`orderPage.periodicRent.${cycle}`);
    },
    periodicRentUnit() {
      const cycle = this.normalizedRentalCycleCode;
      return this.$t(`orderPage.rentCycleUnit.${cycle}`);
    },
    showContact() {
      return this.contactCountState === 'success' && this.needContactCount > 0;
    },
    showContactAdd() {
      return (this.showData?.emergencyContactList || []).length < this.needContactCount;
    },
    repayPlanPreviewItems() {
      const term = Math.max(Number(this.showData?.rentalTerm) || 0, 0);
      if (!term) return [];
      const start = this.normalizeRepayPlanStartDate(this.showData?.rentalStartDate);
      const cycle = this.normalizeCycle(this.tempOrderData?.planUnit || this.tempOrderData?.sku?.rentalCycle);
      const firstAmount = Number(this.showData?.firstRentalAmount || 0);
      const planPrice = Number(this.showData?.planPrice || 0);
      const depositAmount = Number(this.showData?.depositAmount || 0);
      return Array.from({ length: term }, (_, index) => {
        const amount = index === 0 ? addMoney(firstAmount, depositAmount) : planPrice;
        return {
          period: index + 1,
          paidTime: this.shiftRepayPlanDate(start, cycle, index).valueOf(),
          total: amount,
          rentalAmount: amount,
          status: 'PENDING'
        };
      });
    }
  },
  mounted() {
    if (!this.showData) {
      this.$router.replace('/index');
      return;
    }
    this.queryNeedContactCount();
    this.queryAgreements();
    this.handleKycRedirect();
  },
  methods: {
    ...mapActions({
      changeAddress: 'orders/changeAddress',
      changeContactList: 'orders/changeContactList'
    }),
    onClickLeft() {
      this.goBack();
    },
    normalizeCycle(cycle) {
      return String(cycle || '').trim().toUpperCase();
    },
    normalizeRepayPlanStartDate(value) {
      const numericValue = Number(value);
      const startValue = Number.isFinite(numericValue) && numericValue > 0
        ? (numericValue < 100000000000 ? numericValue * 1000 : numericValue)
        : value;
      const start = dayjs(startValue);
      return start.isValid() ? start : dayjs();
    },
    shiftRepayPlanDate(start, cycle, offset) {
      if (offset <= 0) return start;
      if (['MONTH', 'MONTHLY', 'M'].includes(cycle)) {
        const repayDay = start.date() <= 20 ? 1 : 5;
        const nextMonthlyRepayDate = base => {
          let candidate = base.startOf('day').date(repayDay);
          if (!candidate.isAfter(base)) candidate = candidate.add(1, 'month').date(repayDay);
          return candidate;
        };
        let secondPeriodStart = nextMonthlyRepayDate(start);
        const graceEnd = start.add(10, 'day');
        if (graceEnd.isAfter(secondPeriodStart)) secondPeriodStart = nextMonthlyRepayDate(secondPeriodStart);
        return secondPeriodStart.add(offset - 1, 'month');
      }
      const days = RENTAL_CYCLE_DAYS[cycle] || (['BI_WEEK', 'BIWEEKLY', 'BI-WEEKLY', 'BI_WEEKLY', 'B'].includes(cycle) ? 14 : 0);
      if (days > 0) return start.add(days * offset, 'day');
      if (['WEEK', 'WEEKLY', 'W'].includes(cycle)) return start.add(7 * offset, 'day');
      if (['DAY', 'DAILY', 'D'].includes(cycle)) return start.add(offset, 'day');
      return start.add(offset, 'month');
    },
    repayPlanPeriodSuffix(period) {
      const locale = this.$i18n?.locale || localStorage.getItem('language') || 'en';
      if (['id', 'in', 'zh'].includes(String(locale).toLowerCase())) return '';
      const value = Math.max(Number(period) || 0, 0);
      if (value >= 11 && value <= 13) return 'th';
      return {1: 'st', 2: 'nd', 3: 'rd'}[value % 10] || 'th';
    },
    repayPlanPeriodText(period) {
      return this.$t('orderPage.repayPlanPeriodFormat', {
        period,
        suffix: this.repayPlanPeriodSuffix(period)
      });
    },
    openAddressPopup({ showType } = {}) {
      this.$refs.addressPopup.open({ showType: showType || (this.address ? 2 : 1) });
    },
    formatIndonesiaPhone(phone = '') {
      const digits = String(phone || '').replace(/\D/g, '');
      if (!digits) return '';
      const normalized = digits.startsWith('62')
        ? digits
        : `62${digits.replace(/^0/, '')}`;
      const local = normalized.slice(2);
      const groups = [];
      if (local) {
        groups.push(local.slice(0, 3));
        for (let index = 3; index < local.length; index += 4) {
          groups.push(local.slice(index, index + 4));
        }
      }
      return groups.length ? `+62 ${groups.join(' ')}` : '+62';
    },
    contactRelationshipText(contact = {}) {
      const relation = contact.relation || RELATIONSHIP_TYPE.find(item => item.value === contact.relationship);
      if (!relation) return '';
      const locale = this.$i18n?.locale || localStorage.getItem('language') || LANGUAGE.en;
      if (locale === LANGUAGE.id) return relation.textId;
      if (locale === LANGUAGE.ph) return relation.textPh;
      return relation.textEn;
    },
    contactNameText(contact = {}) {
      const name = contact.name || '';
      const relation = this.contactRelationshipText(contact);
      return relation ? `${name} (${relation})` : name;
    },
    onAddressConfirm({ data }) {
      this.changeAddress(data);
    },
    onAddressDelete({ data } = {}) {
      if (data?.id && this.address?.id === data.id) {
        const nextAddress = this.addressList.find(item => item.id !== data.id && item.isDefault) ||
          this.addressList.find(item => item.id !== data.id) ||
          null;
        this.changeAddress(nextAddress);
      }
      this.componentKey = Math.random();
    },
    openContactPopup() {
      this.$refs.contactPopup.open();
    },
    onContactSave({ data }) {
      this.changeContactList(data || []);
    },
    onContactSelectionChange({ data, deletedId } = {}) {
      const removedId = String(deletedId || '');
      const contacts = Array.isArray(data) ? data : [];
      if (removedId) {
        this.changeContactList(contacts.filter(item => String(item.id || item.contactId || '') !== removedId));
        return;
      }
      this.changeContactList(contacts);
    },
    onGoodsImageError() {
      this.goodsImageFailed = true;
    },
    async queryNeedContactCount() {
      if (this.contactCountState === 'loading') return;
      this.contactCountState = 'loading';
      try {
        const res = await getContactCount();
        if (res?.code !== 'Success') {
          throw new Error(res?.message || 'get contact count failed');
        }
        this.needContactCount = Math.max(Number(res.data?.count) || 0, 0);
        this.contactCountState = 'success';
      } catch (e) {
        this.contactCountState = 'failed';
        this.$toast(this.$t('systemError'));
      }
    },
    queryAgreements() {
      getAgreements({ sceneCode: AGREEMENT_SCENE.PLACE_ORDER }).then(res => {
        if (res.code === 'Success') {
          this.agreements = orderConfirmAgreements(res.data.agreements);
        }
      });
    },
    getKycRedirectToken() {
      const { token, bizToken, biz_token } = this.$route.query;
      return token || bizToken || biz_token || sessionStorage.getItem(KYC_AUTH_TOKEN_KEY);
    },
    clearAuthResultQuery() {
      const { authType, token, bizToken, biz_token, ...query } = this.$route.query;
      if (authType || token || bizToken || biz_token) {
        this.$router.replace({ path: this.$route.path, query });
      }
    },
    async handleKycRedirect() {
      if (isKycV2Return(this.$route)) {
        try {
          const view = await completeKycV2Flow(this);
          if (view && !openKycV2ResultPopup(this, view)) {
            clearFinishedKycV2(this);
          }
        } catch (e) {
          console.error('Complete KYC v2 failed:', e);
          this.$toast(this.$t('systemError'));
          clearFinishedKycV2(this);
        }
        return;
      }
      const authType = this.$route.query.authType;
      if (!authType) return;
      const token = this.getKycRedirectToken();
      if (!token) {
        this.$toast(this.$t('systemError'));
        this.clearAuthResultQuery();
        return;
      }
      try {
        const res = await getAuthResult({ token, type: authType });
        if (res.code !== 'Success') {
          this.$toast(res.message || this.$t('systemError'));
          this.clearAuthResultQuery();
          return;
        }
        if (res.data?.verifyResult) {
          this.$refs.authResultPopup.open({
            authType: res.data?.type || authType,
            verifyResult: res.data.verifyResult
          });
        } else {
          this.clearAuthResultQuery();
        }
      } catch (e) {
        console.error('Get auth result failed:', e);
        this.$toast(this.$t('systemError'));
        this.clearAuthResultQuery();
      }
    },
    handleAuthResultSuccess() {
      sessionStorage.removeItem(KYC_AUTH_TOKEN_KEY);
      sessionStorage.setItem(KYC_VERIFIED_KEY, '1');
      clearFinishedKycV2(this);
      this.clearAuthResultQuery();
      this.runOrderPrecheck(true);
    },
    async handleAuthResultRetry() {
      this.clearAuthResultQuery();
      if (hasKycV2Context()) {
        await retryKycV2Flow(this);
        return;
      }
      await this.ensureKycReady();
    },
    handleAuthResultBack() {
      sessionStorage.removeItem(KYC_AUTH_TOKEN_KEY);
      sessionStorage.removeItem(KYC_VERIFIED_KEY);
      clearFinishedKycV2(this);
      this.clearAuthResultQuery();
    },
    savePendingOrder() {
      sessionStorage.setItem(TEMP_ORDER_KEY, JSON.stringify(this.tempOrderData));
    },
    isFeatureEnabled(value) {
      return value === true || value === 'true' || value === 1 || value === '1';
    },
    getSystemFeatures(data = {}) {
      return data.features || data;
    },
    async ensureKycReady() {
      if (sessionStorage.getItem(KYC_VERIFIED_KEY) === '1') return true;
      this.savePendingOrder();
      const ready = await startKycV2Flow(this, {
        businessScene: 'orderConfirm',
        returnPath: '/order/confirm'
      });
      if (ready) sessionStorage.setItem(KYC_VERIFIED_KEY, '1');
      return ready;
    },
    async ensureOrderAdditionalReady() {
      const configRes = await getAppSystemConfig({ platform: 'h5', channel: this.channel || DEFAULT_REG_CHANNEL });
      if (configRes.code !== 'Success') {
        this.$toast(configRes.message || this.$t('systemError'));
        return false;
      }
      const features = this.getSystemFeatures(configRes.data || {});
      if (!this.isFeatureEnabled(features.additionInfo)) return true;
      const requiredTypes = resolveRequiredSupplementTypes(features);
      const statusRes = await queryAdditionalInfo();
      if (statusRes.code !== 'Success') {
        this.$toast(statusRes.message || this.$t('systemError'));
        return false;
      }
      if (canSkipOrderAdditional(statusRes.data, requiredTypes)) return true;
      this.savePendingOrder();
      this.$router.replace({ path: '/additional-info', query: { orderFlow: '1' } });
      return false;
    },
    async runOrderPrecheck(fromKyc = false) {
      if (this.prechecking) return;
      this.prechecking = true;
      try {
        const kycReady = fromKyc || await this.ensureKycReady();
        if (!kycReady) return;
        if (!await this.ensureOrderAdditionalReady()) return;
        sessionStorage.setItem(KYC_VERIFIED_KEY, '1');
        this.$router.replace('/payment/checkout');
      } catch (e) {
        console.error('Order precheck failed:', e);
        this.$toast(this.$t('systemError'));
      } finally {
        this.prechecking = false;
      }
    },
    goCheckout() {
      if (this.prechecking) return;
      if (this.contactCountState !== 'success') {
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
        return;
      }
      if (!this.address) {
        this.$toast({ message: this.$t('orderPage.addressTip'), duration: 3000 });
        return;
      }
      if ((this.showData?.emergencyContactList || []).length !== this.needContactCount) {
        this.$toast({ message: this.$t('orderPage.contactTip'), duration: 3000 });
        if (this.needContactCount > 0) {
          window.setTimeout(() => {
            this.$refs.contactPopup?.open();
          }, 3000);
        }
        return;
      }
      if (!this.agreementChecked) {
        this.$toast({ message: this.$t('orderPage.agreementRequired'), duration: 3000 });
        this.scrollToAgreement();
        return;
      }
      // 新订单重新执行 Android 同步的 KYC 预检查，避免复用上一次订单的临时认证标记。
      sessionStorage.removeItem(KYC_VERIFIED_KEY);
      this.runOrderPrecheck();
    },
    scrollToAgreement() {
      this.$nextTick(() => {
        const target = this.$refs.agreementSection;
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }
}
</script>

<style lang="less" scoped>
.order-confirm {
  min-height: 100vh;
  padding-top: 88px;
  padding-bottom: 324px;
  box-sizing: border-box;
  background: var(--bg-color);

  &__address,
  &__contact {
    .btn-primary {
      margin: 24px 30px 0;
    }
  }

  &__goods,
  &__info,
  &__notes-card {
    background: #FFFFFF;
    padding: 30px 40px;
  }

  &__goods-info-img {
    width: 160px;
    height: 160px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 8px;
    border: 1px solid var(--border-color-base);
    overflow: hidden;
  }

  &__goods-info-name {
    width: 480px;
    font-weight: 500;
    font-size: 26px;
    line-height: 38px;
  }

  &__goods-info-sku {
    margin-top: 12px;
    font-size: 26px;
    color: #999999;
    line-height: 32px;
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 30px 30px 50px;
    background: var(--bg-color);

    .btn-primary {
      border-radius: 14px;
    }

    .checkout-hint {
      margin-top: 12px;
      padding: 0 10px;
      font-size: 22px;
      line-height: 30px;
      color: var(--text-color-gray);
      text-align: center;
    }
  }
}

.agreement-check {
  margin-bottom: 18px;
  color: var(--text-color-black);
  font-size: 24px;
  line-height: 34px;
}

.contact-count-state {
  padding: 24px 30px 0;
  color: var(--text-color-gray);
  font-size: 26px;
  line-height: 36px;
  text-align: center;

  &--error .btn-primary {
    margin: 18px auto 0;
    width: 220px;
  }
}

.address-info,
.contact-info {
  padding: 30px 60px;
  background: #FFFFFF;

  &.next {
    background: #FFFFFF url(../../assets/img/next.png) right 30px center no-repeat;
    background-size: auto 24px;
  }
}

.address-info {
  &__img {
    width: 32px;
    height: 32px;
  }

  &__text {
    margin-left: 20px;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);

    .b-text {
      font-size: 28px;
      color: var(--text-color-black);
      margin-right: 20px;
      font-weight: 500;
    }
  }
}

.contact-info {
  .icon-friends {
    font-size: 32px;
    color: var(--primary-color);
  }

  &__text {
    margin-left: 20px;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);
  }

  .contact-item {
    word-break: break-all;

    &__name,
    &__phone {
      display: block;
    }
  }

  &__text-title {
    font-size: 32px;
    color: var(--text-color-black);
  }
}

.goods-plan {
  margin-top: 27px;

  &__left,
  &__right {
    width: 280px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
    border-radius: var(--border-radius-base);
    text-align: center;
    padding-bottom: 24px;

    img {
      width: 32px;
      height: 32px;
      display: block;
      margin: 32px auto 0;
    }

    .desc {
      margin-top: 20px;
      font-size: 26px;
      color: #999999;
      line-height: 32px;
    }

    .date {
      margin-top: 13px;
      font-size: 28px;
      color: var(--primary-color);
      line-height: 32px;
      font-weight: 500;
    }
  }

  &__tips {
    margin-top: 12px;
    font-size: 20px;
    color: var(--text-color-gray);
    line-height: 32px;
    text-align: center;
  }

  &__arrow {
    width: 0;
    height: 0;
    border-top: 20px solid transparent;
    border-bottom: 20px solid transparent;
    border-left: 20px solid #999999;
  }
}

.text-total {
  font-size: 42px;
  line-height: 48px;
}

.org {
  color: var(--red-color);
}

.text-left,
.text-right {
  font-size: 26px;
  line-height: 32px;
}

.text-left {
  color: var(--text-color-gray);
}

.text-right {
  color: var(--text-color-black);
}

.agreement {
  padding-bottom: 20px;
  font-size: 26px;
  line-height: 30px;
  color: var(--text-color-gray);

  > span {
    color: var(--link-color);
  }
}
</style>

<style lang="less" scoped>
  .order-confirm {
    min-height: 100vh;
    padding-top: 92px;
    padding-bottom: calc(324px + env(safe-area-inset-bottom));
    background: var(--bg-color);

  ::v-deep .van-nav-bar { height: 92px; background: #fff; }
  ::v-deep .van-nav-bar__title { color: #172554; font-size: 30px; font-weight: 600; }
  ::v-deep .van-nav-bar .van-icon { color: #172554; font-size: 40px; }

  &__address,
  &__contact,
  &__goods,
  &__dates,
  &__info,
  &__notes-card,
  &__agreement {
    margin: 16px 16px 0;
    padding: 16px;
    border: 2px solid #E5EAF3;
    border-radius: 20px;
    background: #fff;
    box-sizing: border-box;
  }

  &__agreement { margin-bottom: 32px; }
  &__notes-card + &__notes-card { margin-top: 16px; }

  &__remaining-notice {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    padding: 16px;
    border-radius: 16px;
    background: #EAF2FF;
    box-sizing: border-box;
  }

  &__remaining-notice-icon {
    flex: 0 0 auto;
    color: #0B45FF;
    font-size: 36px;
  }

  &__remaining-notice-title {
    color: #172554;
    font-size: 26px;
    line-height: 36px;
    font-weight: 700;
  }

  &__remaining-notice-desc {
    margin-top: 4px;
    color: #6B7280;
    font-size: 22px;
    line-height: 32px;
  }

  &__safety {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 16px 16px 0;
    padding: 16px;
    border-radius: 20px;
    background: #EAF2FF;
    box-sizing: border-box;
  }

  &__safety-icon {
    flex: 0 0 auto;
    color: #0B45FF;
    font-size: 40px;
  }

  &__safety-title {
    color: #172554;
    font-size: 26px;
    line-height: 36px;
    font-weight: 500;
  }

  &__safety-subtitle {
    margin-top: 4px;
    color: #6B7280;
    font-size: 22px;
    line-height: 32px;
  }

  &__section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
    color: #172554;
    font-size: 30px;
    line-height: 40px;
    font-weight: 500;
  }

  &__section-title-main {
    display: flex;
    align-items: center;
    gap: 12px;

    .van-icon {
      color: #0B45FF;
      font-size: 36px;
    }
  }

  &__edit {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #0B45FF;
    line-height: 1;

    font-size: 24px;
    font-weight: 500;

    .van-icon { font-size: 28px; }
  }

  &__divider {
    height: 2px;
    margin: 12px 0 16px;
    background: #E5EAF3;
  }

  &__address .btn-primary,
  &__contact .btn-primary { margin: 0; }
  &__goods-info-img {
    position: relative;
    flex: 0 0 144px;
    width: 144px;
    height: 144px;
    border-width: 2px;
    border-radius: 16px;
    background: #F3F6FC url('../../assets/img/order/empty.png') center / 72px 72px no-repeat;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: #fff;
    }

    &.is-empty { background-color: #F3F6FC; }
  }
  &__goods-info-name { width: auto; font-size: 26px; line-height: 36px; }
  &__goods-info-copy { min-width: 0; overflow: hidden; }
  &__goods-info-name { min-width: 0; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__goods-info-sku { margin-top: 8px; font-size: 24px; line-height: 32px; overflow-wrap: anywhere; word-break: break-word; }
  &__footer {
    z-index: 20;
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
    border-top: 2px solid #E5EAF3;
    background: #fff;
    .footer-amount {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
      color: #172554;
      font-size: 30px;
      line-height: 44px;

      strong {
        color: #0B45FF;
        font-size: 36px;
        font-weight: 700;
      }
    }
    .btn-primary { height: 96px; margin-top: 16px; border-radius: 16px; font-size: 30px; line-height: 92px; }
    .footer-description {
      margin-top: 0;
      color: #6B7280;
      font-size: 24px;
      line-height: 34px;
    }
    .checkout-hint { margin-top: 16px; padding: 0 16px; font-size: 24px; line-height: 34px; }
  }
}

.address-info,
.contact-info { min-width: 0; padding: 0; background: #fff; }
.address-info__text,
.contact-info__text {
  min-width: 0;
  padding-left: 32px;
  color: #020B3A;
  font-size: 26px;
  line-height: 36px;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.address-info__name,
.address-info__phone,
.address-info__detail {
  color: #020B3A;
  font-size: 26px;
  line-height: 36px;
  font-weight: 400;
}
.address-info__phone,
.address-info__detail {
  margin-top: 8px;
}
.contact-info .icon-friends { font-size: 36px; }
.contact-info__text-title { font-size: 30px; }
.contact-info__required { margin-bottom: 12px; color: #6B7280; font-size: 22px; line-height: 30px; }
.contact-info__required--empty { margin: 0 0 16px; }
.contact-item {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;

  & + & {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #E5EAF3;
  }

  &__name,
  &__phone {
    color: #020B3A;
    font-size: 26px;
    line-height: 36px;
    font-weight: 400;
  }

  &__phone {
    margin-top: 8px;
  }
}
.contact-count-state { padding: 16px; font-size: 26px; line-height: 36px; }
.goods-plan { gap: 16px; margin-top: 16px; }
.goods-plan__left,
.goods-plan__right { width: auto; flex: 1; padding: 16px; border: 2px solid #E5EAF3; border-radius: 16px; box-shadow: none; }
.goods-plan__left img,
.goods-plan__right img { width: 36px; height: 36px; margin: 0 auto 8px; }
.goods-plan__left .desc,
.goods-plan__right .desc { margin-top: 8px; font-size: 22px; line-height: 32px; }
  .goods-plan__left .date,
  .goods-plan__right .date { margin-top: 8px; font-size: 26px; line-height: 36px; }
  .goods-plan__left .estimated,
  .goods-plan__right .estimated { margin-top: 4px; color: #6B7280; font-size: 22px; line-height: 30px; }
  .goods-plan__arrow { display: none; }
  .goods-plan__tips { display: flex; align-items: flex-start; gap: 8px; margin-top: 8px; color: #6B7280; font-size: 22px; line-height: 32px; }
  .goods-plan__tips .van-icon { flex: 0 0 auto; margin-top: 4px; font-size: 24px; }
.text-total { font-size: 30px; line-height: 44px; }
.text-left,
.text-right { min-width: 0; font-size: 26px; line-height: 36px; }
.text-right { text-align: right; overflow-wrap: anywhere; }
.order-confirm__price-row small {
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}
.order-confirm__price-row--total .text-right {
  color: #0B45FF;
}
.order-confirm__plan-link {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 16px 0 0;
  border: 0;
  border-top: 2px solid #E5EAF3;
  background: transparent;
  color: #3155A5;
  font-size: 28px;
  line-height: 40px;
  text-align: left;
}
.order-confirm__plan-link > span { flex: 1; }
.order-confirm__plan-link > .van-icon { font-size: 32px; }
.order-confirm__notes-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #172554;
  font-size: 30px;
  line-height: 40px;
  font-weight: 500;
}
.order-confirm__notes-heading .van-icon { color: #0B45FF; font-size: 36px; }
.order-confirm__notes-body,
.order-confirm__notes-list {
  margin-top: 16px;
  color: #6B7280;
  font-size: 24px;
  line-height: 32px;
}
.order-confirm__notes-list { margin-bottom: 0; padding-left: 28px; list-style: none; }
.order-confirm__notes-list li { position: relative; margin-top: 8px; padding-left: 8px; }
.order-confirm__notes-list li:first-child { margin-top: 0; }
.order-confirm__notes-list li::before {
  position: absolute;
  top: 12px;
  left: -20px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0B45FF;
  content: '';
}
.agreement-check { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; font-size: 26px; line-height: 36px; }
.agreement-checkbox { flex: 0 0 auto; margin-top: 2px; }
.agreement-summary,
.agreement-extra { min-width: 0; color: #6B7280; font-size: 26px; line-height: 36px; overflow-wrap: anywhere; }
.agreement-summary > span,
.agreement-extra > span { color: var(--link-color); cursor: pointer; }
.agreement-more { padding: 0; border: 0; background: transparent; color: var(--link-color); font: inherit; cursor: pointer; }
.agreement-summary .agreement-more-prefix { color: #6B7280; }
.agreement-extra { padding-left: 44px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

.order-confirm__repay-plan-popup {
  width: calc(100vw - 64px);
  max-width: 670px;
  max-height: 66vh;
  border-radius: 28px;
  overflow: hidden;
}
.order-confirm__plan-sheet {
  box-sizing: border-box;
  display: flex;
  max-height: 66vh;
  flex-direction: column;
  padding: 24px;
  color: #172554;
}
.order-confirm__plan-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 2px solid #E5EAF3;
}
.order-confirm__plan-sheet-header strong {
  color: #070D3F;
  font-size: 32px;
  line-height: 44px;
  font-weight: 500;
}
.order-confirm__plan-sheet-header .van-icon { color: #64748B; font-size: 32px; }
.order-confirm__plan-sheet-body {
  min-height: 0;
  overflow-y: auto;
  padding-top: 24px;
}
.order-confirm__plan-sheet-notice {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background: #EAF2FF;
}
.order-confirm__plan-sheet-notice--payment {
  margin-top: 24px;
  background: #FFF4E6;
}
.order-confirm__plan-sheet-notice-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #FFFFFF;
  color: #0B45FF;
}
.order-confirm__plan-sheet-notice--payment .order-confirm__plan-sheet-notice-icon {
  color: #F97316;
  background: #FFE0B8;
}
.order-confirm__plan-sheet-notice-icon .van-icon { font-size: 30px; }
.order-confirm__plan-sheet-notice > span:last-child {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}
.order-confirm__plan-sheet-notice strong {
  color: #070D3F;
  font-size: 26px;
  line-height: 36px;
  font-weight: 500;
}
.order-confirm__plan-sheet-notice small {
  margin-top: 4px;
  color: #64748B;
  font-size: 24px;
  line-height: 32px;
  font-weight: 400;
}
.order-confirm__plan-sheet-state { padding: 48px 0; color: #64748B; font-size: 26px; text-align: center; }
.order-confirm__plan-list {
  display: grid;
  gap: 16px;
  margin-top: 24px;
}
.order-confirm__plan-item {
  box-sizing: border-box;
  width: 100%;
  padding: 20px;
  border: 2px solid #E5EAF3;
  border-radius: 16px;
  background: #FFFFFF;
}
.order-confirm__plan-period {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 14px;
  color: #0B45FF;
  background: #EAF3FF;
  font-size: 24px;
  line-height: 32px;
  font-weight: 400;
}
.order-confirm__plan-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  color: #64748B;
  font-size: 24px;
  line-height: 32px;
}
.order-confirm__plan-row strong {
  flex-shrink: 0;
  color: #0B1440;
  font-weight: 400;
  text-align: right;
}
.order-confirm__plan-row .order-confirm__plan-amount {
  color: #0B45FF;
}
</style>
